import { computed, Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { joinPath } from '../../functions/join-path.function';
import { appConfig } from 'src/config';
import { appData } from 'src/assets/common/config/app-data.g';
import { IAppConfigJson } from './models/app-config.-json';
import { IBuildInfo } from './models/build-info';

export interface IConfigService {
    app_subTitle$$: Signal<string | null>;
    app_title$$: Signal<string | null>;
    app_version$$: Signal<string | null>;
    app_versionDetail$$: Signal<string | null>;
    controllerNameAuth$$: Signal<string | null>;
    image_Extensions$$: Signal<string | null>;
    image_MaxHeight$$: Signal<number | null>;
    image_MaxMB$$: Signal<number | null>;
    image_MaxWidth$$: Signal<number | null>;
    logo_extension$$: Signal<string | null>;
    logoEmail_fileName$$: Signal<string | null>;
    logoEmail_name$$: Signal<string | null>;
    logoIconAppleTouch_fileName$$: Signal<string | null>;
    logoIconPng_fileName$$: Signal<string | null>;
    logoIconSvg_fileName$$: Signal<string | null>;
    logoIcon_fileName$$: Signal<string | null>;
    logoThumb_fileName$$: Signal<string | null>;
    logoThumb_name$$: Signal<string | null>;
    logo_fileName$$: Signal<string | null>;
    logo_name$$: Signal<string | null>;
    logos_folderName$$: Signal<string | null>;
    nameApi$$: Signal<string | null>;
    originApi$$: Signal<string | null>;
    pathSeperator$$: Signal<string | null>;
    rootApi$$: Signal<string | null>;
    rootApp$$: Signal<string | null>;
    rootAuth$$: Signal<string | null>;
    rootPublic$$: Signal<string | null>;
    showHide_enter_time$$: Signal<string | null>;
    showHide_leave_time$$: Signal<string | null>;
}

@Injectable({
    providedIn: 'root'
})
export class ConfigService implements IConfigService {
    app_subTitle$$              = signal<string | null>(null);
    app_title$$                 = signal<string | null>(null);
    app_version$$               = signal<string | null>(null);
    app_versionDetail$$         = signal<string | null>(null);
    controllerNameAuth$$        = signal<string | null>(null);
    image_Extensions$$          = signal<string | null>(null);
    image_MaxHeight$$           = signal<number | null>(null);
    image_MaxMB$$               = signal<number | null>(null);
    image_MaxWidth$$            = signal<number | null>(null);
    logo_extension$$            = signal<string | null>(null);
    logoEmail_name$$            = signal<string | null>(null);
    logoIconAppleTouch_fileName$$ = signal<string | null>(null);
    logoIconPng_fileName$$      = signal<string | null>(null);
    logoIconSvg_fileName$$      = signal<string | null>(null);
    logoIcon_fileName$$         = signal<string | null>(null);
    logoThumb_name$$            = signal<string | null>(null);
    logo_name$$                 = signal<string | null>(null);
    logos_folderName$$          = signal<string | null>(null);
    nameApi$$                   = signal<string | null>(null);
    originApi$$                 = signal<string | null>(null);
    pathSeperator$$             = signal<string | null>(null);
    rootApp$$                   = signal<string | null>(null);
    rootPublic$$                = signal<string | null>(null);
    showHide_enter_time$$       = signal<string | null>(null);
    showHide_leave_time$$       = signal<string | null>(null);

    rootApi$$ = computed(() => {
        const o = this.originApi$$(); const n = this.nameApi$$();
        return o && n ? joinPath([o, n], '/') : null;
    });
    rootAuth$$ = computed(() => {
        const r = this.rootApi$$(); const c = this.controllerNameAuth$$();
        return r && c ? joinPath([r, c], '/') : null;
    });
    logo_fileName$$ = computed(() => {
        const n = this.logo_name$$(); const e = this.logo_extension$$();
        return n && e ? `${n}.${e}` : null;
    });
    logoEmail_fileName$$ = computed(() => {
        const n = this.logoEmail_name$$(); const e = this.logo_extension$$();
        return n && e ? `${n}.${e}` : null;
    });
    logoThumb_fileName$$ = computed(() => {
        const n = this.logoThumb_name$$(); const e = this.logo_extension$$();
        return n && e ? `${n}.${e}` : null;
    });

    constructor(private http: HttpClient) {}

    async loadConfig(): Promise<void> {
        // appData (gegenereerd vanuit appsettings.json — leidend voor bestandsnamen)
        this.logo_extension$$.set(appData.extension_Logo);
        this.image_Extensions$$.set(appData.image_Extensions);
        this.image_MaxHeight$$.set(appData.image_MaxHeight);
        this.image_MaxMB$$.set(appData.image_MaxMB);
        this.image_MaxWidth$$.set(appData.image_MaxWidth);
        this.logoEmail_name$$.set(appData.logoEmail_Name);
        this.logoThumb_name$$.set(appData.logoThumb_Name);
        this.logo_name$$.set(appData.logo_Name);

        // appConfig (src/config.ts)
        this.controllerNameAuth$$.set(appConfig.controllerNameAuth);
        this.logoIconAppleTouch_fileName$$.set(appConfig.logoIconAppleTouch_fileName);
        this.logoIconPng_fileName$$.set(appConfig.logoIconPng_fileName);
        this.logoIconSvg_fileName$$.set(appConfig.logoIconSvg_fileName);
        this.logoIcon_fileName$$.set(appConfig.logoIcon_fileName);
        this.showHide_enter_time$$.set(appConfig.showHide_enter_time);
        this.showHide_leave_time$$.set(appConfig.showHide_leave_time);

        // config.json (omgevingsspecifiek, async)
        const __configJson = await firstValueFrom(
            this.http.get<IAppConfigJson>('/assets/config.json')
        );

        this.app_subTitle$$.set(__configJson.subTitle ?? null);
        this.app_title$$.set(__configJson.title ?? null);
        this.logos_folderName$$.set(__configJson.logos_folderName ?? null);
        this.nameApi$$.set(__configJson.nameApi ?? null);
        const _origin = __configJson.originApi;
        this.originApi$$.set(_origin ? _origin : window.location.origin);
        this.pathSeperator$$.set(__configJson.pathSeperator ?? null);
        this.rootApp$$.set(__configJson.rootApp ?? null);
        this.rootPublic$$.set(__configJson.rootPublic ?? null);

        // build-info.json (gegenereerd per build/serve uit git — zie scripts/gen-build-info.mjs).
        // Zit in de "common"-assets, dus identiek voor elke omgeving die van dezelfde commit
        // gebouwd is; ontbreekt het (bv. nog niet gegenereerd), dan blijft de versie leeg.
        // Weergave = jaar-omgeving-teller (spiegelt het oude handmatige "2026-dev001", maar nu
        // automatisch via het aantal commits); het exacte commit/branch komt in de tooltip.
        const __buildInfo = await firstValueFrom(
            this.http.get<IBuildInfo>('/assets/build-info.json')
        ).catch(() => null);
        const _env = __configJson.env;
        if (__buildInfo && _env) {
            const _year = new Date(__buildInfo.builtAt).getFullYear();
            const _counter = String(__buildInfo.buildNumber).padStart(3, '0');
            this.app_version$$.set(`${_year}-${_env}${_counter}`);
        } else {
            this.app_version$$.set(null);
        }
        this.app_versionDetail$$.set(
            __buildInfo
                ? `${__buildInfo.branch}@${__buildInfo.commit}${__buildInfo.dirty ? '+dirty' : ''} · ${__buildInfo.builtAt}`
                : null
        );
    }
}
